import React, { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  TextField,
  Autocomplete,
} from "@mui/material";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { getAllUsers } from "../../redux/slices/users.slice";
import { getAllScheduleFines } from "../../redux/slices/scheduleFine.slice";
import {
  clearReportsData,
  getReportData,
  onChangeReportsList,
} from "../../redux/slices/reports.slice";
import { reportTypes, statePerformanceColumns } from "../../utils/constants";
import { getAllSubAgents } from "../../redux/slices/agents.slice";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { fineReportColumns, collectionColumns } from "../../utils/constants";
import { Download } from "@mui/icons-material";

interface FineFilter {
  startDate: string | null;
  endDate: string | null;
  fineStatus?: string;
  createdBy?: string;
  agent?: string;
  offenceCode: string;
  pageNumber: number;
  perPage: number;
  sortingOn: string;
  isDescending: boolean;
  warden?: string;
}

const FinesReportForm: React.FC = () => {
  const [reportType, setReportType] = useState("Fines Report");
  const { data: user } = useSelector((state: RootState) => state.auth);

  const [formData, setFormData] = useState<FineFilter>({
    startDate: "",
    endDate: "",
    fineStatus: "all",
    createdBy: "all",
    agent: "all",
    offenceCode: "all",
    pageNumber: 1,
    perPage: 10,
    sortingOn: "Id",
    isDescending: true,
  });

  const dispatch = useDispatch<AppDispatch>();

  const { data: users, loading: usersLoading } = useSelector(
    (state: RootState) => state.users
  );
  const { list: offences, loading: offencesLoading } = useSelector(
    (state: RootState) => state.scheduleFine
  );
  const { subAgents, loading: agentsLoading } = useSelector(
    (state: RootState) => state.agents
  );
  const { pageProps, data, loading } = useSelector(
    (state: RootState) => state.reports
  );

  useEffect(() => {
    if (!users.length) {
      dispatch(getAllUsers());
    }
  }, [users.length, dispatch]);

  useEffect(() => {
    if (!subAgents.length && reportType === "Collection Report") {
      dispatch(getAllSubAgents());
    }
  }, [subAgents.length, dispatch, reportType]);

  useEffect(() => {
    if (!offences.length) {
      dispatch(getAllScheduleFines());
    }
  }, [offences.length, dispatch]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = () => {
    const basePayload = {
      ...formData,
      startDate: formData.startDate
        ? dayjs(formData.startDate).startOf("day").toISOString()
        : null,
      endDate: formData.endDate
        ? dayjs(formData.endDate).endOf("day").toISOString()
        : null,
    };

    const finalPayload =
      reportType === "Collection Report"
        ? {
            startDate: basePayload.startDate,
            endDate: basePayload.endDate,
            agent: basePayload.agent,
            offenceCode: basePayload.offenceCode,
            pageNumber: 1,
            perPage: 10,
            sortingOn: "CreatedDate",
            isDescending: true,
          }
        : basePayload;

    dispatch(
      onChangeReportsList({ ...pageProps, ...finalPayload, reportType })
    );
    dispatch(getReportData({ reportType, ...finalPayload } as any));
  };

  const generatePdf = (data: any) => {
    if (!data) return;
    const doc = new jsPDF({ orientation: "landscape" });

    const companyName = "SLRSA";
    const title = reportType;
    const color = "#1691ff";
    const generatedAt = dayjs().format("YYYY-MM-DD HH:mm:ss");

    const pageWidth = doc.internal.pageSize.getWidth();

    // === Header ===
    doc.setTextColor(color);
    doc.setFontSize(18);
    doc.text(companyName, 15, 15); // Company name - left aligned

    // Draw horizontal border under header
    doc.setDrawColor(22, 145, 255);
    doc.setLineWidth(0.5);
    doc.line(15, 18, pageWidth - 15, 18); // Border line

    // === Title (below border, centered) ===
    doc.setFontSize(14);
    doc.setTextColor(0);
    doc.text(title, pageWidth / 2, 26, { align: "center" }); // Title below border

    // === Table Data ===
    const columns =
      reportType === "Collection Report"
        ? collectionColumns
        : reportType === "Fines Report"
        ? fineReportColumns
        : statePerformanceColumns;

    const headers = [["S.No", ...columns.map((col) => col.label)]];

    const rows = data.map((row: any, index: number) => [
      index + 1, // S.No
      ...columns.map((col) => {
        const val = row[col.id];
        if (!val) return "";
        if (col.id.toLowerCase().includes("date")) {
          return dayjs(val).format("DD/MM/YYYY HH:mm");
        } else if (typeof val === "number") {
          return val.toLocaleString();
        }
        return val.toString();
      }),
    ]);

    autoTable(doc, {
      head: headers,
      body: rows,
      startY: 32,
      theme: "grid",
      headStyles: {
        fillColor: [22, 145, 255],
        textColor: 255,
        fontSize: 10,
      },
      styles: {
        fontSize: 9,
      },
      didDrawPage: (data: any) => {
        const pageCount = doc.getNumberOfPages();
        const pageHeight = doc.internal.pageSize.height;

        // Footer: page number (left)
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(
          `Page ${doc.getCurrentPageInfo().pageNumber} of ${pageCount}`,
          data.settings.margin.left,
          pageHeight - 5
        );

        // Footer: generated timestamp (right)
        doc.text(
          `Generated at: ${generatedAt}`,
          pageWidth - 15,
          pageHeight - 5,
          { align: "right" }
        );
      },
    });

    doc.save(`${title.replace(" ", "_")}.pdf`);
    dispatch(getReportData({ ...pageProps, perPage: 10 }));
  };

  return (
    <Grid container spacing={2} className="pt-4 pb-2 flex items-center">
      {/* Report Type */}
      <Grid item xs={6} md={2}>
        <Autocomplete
          fullWidth
          options={reportTypes
            .filter((i) => i.permissins.includes(user?.UserRole ?? ""))
            .map((rt: any) => rt.label)}
          value={reportType}
          onChange={(e, value) => {
            dispatch(clearReportsData());
            dispatch(onChangeReportsList({ ...pageProps, reportType: value }));
            setReportType(value || "Fines Report");
            setFormData((prev) => ({
              ...prev,
              agent: "all",
              createdBy: "all",
              fineStatus: "all",
              offenceCode: "all",
            }));
          }}
          renderInput={(params) => (
            <TextField {...params} label="Report Type" size="small" />
          )}
        />
      </Grid>

      {/* Start Date */}
      <Grid item xs={6} md={2}>
        <TextField
          type="date"
          fullWidth
          label="Start Date"
          name="startDate"
          value={formData.startDate || ""}
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
      </Grid>

      {/* End Date */}
      <Grid item xs={6} md={2}>
        <TextField
          type="date"
          fullWidth
          label="End Date"
          name="endDate"
          value={formData.endDate || ""}
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
      </Grid>

      {/* Fine Status (Fines Report only) */}
      {reportType === "Fines Report" && (
        <Grid item xs={6} md={2}>
          <TextField
            select
            fullWidth
            label="Status"
            name="fineStatus"
            value={formData.fineStatus}
            size="small"
            onChange={handleChange}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="Pending">Pending</MenuItem>
            <MenuItem value="Paid">Paid</MenuItem>
          </TextField>
        </Grid>
      )}

      {/* Created By / RSCOfficer (Fines Report only) */}
      {reportType === "Fines Report" && (
        <Grid item xs={6} md={2}>
          <Autocomplete
            fullWidth
            loading={usersLoading}
            options={[
              "All",
              ...(users
                ?.filter((u: any) => u.roleName?.toLowerCase() === "warden")
                .map((u: any) => u.name) || []),
            ]}
            value={formData.createdBy === "all" ? "All" : formData.createdBy}
            onChange={(e, value) =>
              setFormData((prev) => ({
                ...prev,
                createdBy: !value || value === "All" ? "all" : value,
              }))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="RSCOfficer"
                size="small"
                placeholder="Select RSCOfficer"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {usersLoading ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>
      )}

      {/* Agent (Collection Report only) */}
      {reportType === "Collection Report" && (
        <Grid item xs={6} md={2}>
          <Autocomplete
            fullWidth
            loading={agentsLoading}
            options={[
              "All",
              ...(subAgents?.map(
                (agent: any) => agent.firstName + " " + agent?.lastName
              ) || []),
            ]}
            value={formData.agent === "all" ? "All" : formData.agent}
            onChange={(e, value) =>
              setFormData((prev) => ({
                ...prev,
                agent: !value || value === "All" ? "all" : value,
              }))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Agent"
                size="small"
                placeholder="Select Agent"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {agentsLoading ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>
      )}

      {/* Offense Code (Both) */}
      {reportType !== "Station Performance Report" && (
        <Grid item xs={6} md={2}>
          <Autocomplete
            fullWidth
            loading={offencesLoading}
            options={[
              "All",
              ...(offences?.map((o: any) => o.offenceCode) || []),
            ]}
            value={
              formData.offenceCode === "all" ? "All" : formData.offenceCode
            }
            onChange={(e, value) =>
              setFormData((prev) => ({
                ...prev,
                offenceCode: !value || value === "All" ? "all" : value,
              }))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Offense Code"
                size="small"
                placeholder="Select Offense"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {offencesLoading ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>
      )}

      {/* Submit Button */}
      <Grid item xs={12} md={3} className="flex gap-2 items-center">
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={loading}
        >
          Submit
        </Button>
        {data.list.length > 0 && (
          <Button
            variant="outlined"
            color="error"
            onClick={() => {
              dispatch(
                getReportData({ ...pageProps, perPage: data.totalRecords })
              ).then((res: any) => {
                if (res.payload?.responseCode === "200") {
                  generatePdf(res.payload?.result?.list ?? []);
                }
              });
            }}
            startIcon={<Download />}
            disabled={loading}
          >
            Download PDF
          </Button>
        )}
      </Grid>
    </Grid>
  );
};

export default FinesReportForm;
